import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const Table_Size = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">📏 CSS Table Size</h1>

      <p className="tutorial-intro">
        CSS allows you to control the size of tables, columns, and rows. You can
        set the overall width and height of a table, adjust column widths, and
        increase row heights for better readability. This helps keep your table
        layout clean and consistent across devices.
      </p>

      {/* Intro Highlight Box */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "#92400e", marginBottom: "0.5rem" }}>
          🌟 Why Control Table Size?
        </h2>
        <p style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
          By defining table sizes, you make sure your content stays aligned,
          avoids overflow, and looks professional across different screen sizes.
        </p>
      </div>

      {/* Table Width */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 Table Width</h2>
        <p>
          Use the <code>width</code> property to set how wide a table should be.
          You can use fixed pixel values or percentages.
        </p>

        <CodeCopyBlock
          code={`table {
  width: 100%;
}

th, td {
  border: 1px solid black;
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "1px solid black",
            }}
          >
            <thead>
              <tr>
                <th style={{ border: "1px solid black", padding: "8px" }}>
                  Name
                </th>
                <th style={{ border: "1px solid black", padding: "8px" }}>
                  Age
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ border: "1px solid black", padding: "8px" }}>
                  Ravi
                </td>
                <td style={{ border: "1px solid black", padding: "8px" }}>
                  24
                </td>
              </tr>
              <tr>
                <td style={{ border: "1px solid black", padding: "8px" }}>
                  Neha
                </td>
                <td style={{ border: "1px solid black", padding: "8px" }}>
                  28
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Column Width */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 Column Width</h2>
        <p>
          Apply <code>width</code> directly to <code>th</code> or{" "}
          <code>td</code> elements to control column size.
        </p>

        <CodeCopyBlock
          code={`th:first-child {
  width: 70%;
}

th:last-child {
  width: 30%;
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "1px solid black",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    width: "70%",
                    border: "1px solid black",
                    padding: "8px",
                  }}
                >
                  Description
                </th>
                <th
                  style={{
                    width: "30%",
                    border: "1px solid black",
                    padding: "8px",
                  }}
                >
                  Price
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ border: "1px solid black", padding: "8px" }}>
                  Coffee
                </td>
                <td style={{ border: "1px solid black", padding: "8px" }}>
                  $3
                </td>
              </tr>
              <tr>
                <td style={{ border: "1px solid black", padding: "8px" }}>
                  Sandwich
                </td>
                <td style={{ border: "1px solid black", padding: "8px" }}>
                  $5
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Row Height */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 Row Height</h2>
        <p>
          The <code>height</code> property can be used to control row or cell
          height.
        </p>

        <CodeCopyBlock
          code={`td {
  height: 100px;
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "1px solid black",
            }}
          >
            <tbody>
              <tr>
                <td
                  style={{
                    border: "1px solid black",
                    height: "100px",
                    padding: "8px",
                  }}
                >
                  Tall Row
                </td>
                <td
                  style={{
                    border: "1px solid black",
                    height: "50px",
                    padding: "8px",
                  }}
                >
                  Example
                </td>
              </tr>
              <tr>
                <td style={{ border: "1px solid black", padding: "8px" }}>
                  Normal
                </td>
                <td style={{ border: "1px solid black", padding: "8px" }}>
                  Row
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Fixed Table Layout */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 Fixed Table Layout</h2>
        <p>
          With <code>table-layout: fixed;</code>, all columns take equal width
          regardless of content.
        </p>

        <CodeCopyBlock
          code={`table {
  table-layout: fixed;
  width: 100%;
}

td {
  overflow: hidden;
  text-overflow: ellipsis;
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "1px solid black",
              tableLayout: "fixed",
            }}
          >
            <tbody>
              <tr>
                <td style={{ border: "1px solid black", padding: "8px" }}>
                  This is a very long piece of text that will be cut off
                </td>
                <td style={{ border: "1px solid black", padding: "8px" }}>
                  Short
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          You can now control table width, column width, and row height. Next,
          try exploring{" "}
          <a href="/css/table-borders" className="learn-more">
            Table Borders
          </a>{" "}
          to customize how your table outlines look.
        </p>
      </div>
    </div>
  );
};

export default Table_Size;
